import React from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import _camelCase from "lodash/camelCase";
import { logCustomEvent as brazeLogCustomEvent } from "@braze/web-sdk";
import { BrazeCustomEvents } from "shared-services";
import { MediaRequestType } from "shared-types";
import { i18n, Trans } from "i18n";
import {
  AccountType,
  BreadcrumbBar,
  IBreadcrumbBarItem,
  StepTracker,
  StepTrackerItemStatus,
  withScrollManager,
  Head,
  myAccountLink,
  StickyCTABar,
  SUPPORTED_VIDEO_EXTENSIONS,
  SUPPORTED_AUDIO_EXTENSIONS,
  TitaniumUploader,
  IFileUpload,
} from "component-library";
import { ICompleteFileUpload } from "component-library/src/components/Organisms/TitaniumUploader/TitaniumFileUploader";
import {
  ISelfTapeRequest,
  ISubmission,
  ISubmissionDetails,
  IVideoRequired,
} from "shared/models/selfTapeRequest";
import selfTapeRequestService from "shared/services/api/selfTapeRequest/selfTapeRequestService";

import dateService from "shared/services/dateService";

import { MapDispatchToProps } from "shared/types/utilities";
import PageKey from "config/PageKey";
import { IApplicationState } from "store";
import { getSelfTapeUploadPage } from "store/page/page.actions";
import { GetSelfTapeUploadPage } from "store/page/page.types";
import { getPageTranslation } from "store/translation/translation.actions";

import { GetPageTranslation } from "store/translation/translation.types";

import { hidePopup, setLoading, showPopup } from "store/ui/ui.actions";

import { HidePopup, PopupType, SetLoading, ShowPopup } from "store/ui/ui.types";

import { UrlCommand } from "types/UrlCommand";

import { ISelfTapeUploadPageTranslation } from "models/translation";

import withPageData, {
  ILoaders,
  IWithPageDataProps,
  mapStateToWithPageDataProps,
} from "hoc/withPageData";
import {
  getConfirmationPopupTexts,
  getMediaPopupTexts,
  getUploaderTexts,
  MediaErrorPopupType,
  prepareBreadcrumbs,
  prepareTexts,
} from "pages/SelfTapeUploadPage/SelfTapeUploadPage.helpers";
import "./SelfTapeUploadPage.scss";
import ConfirmSendSelfTape from "pages/SelfTapeUploadPage/ConfirmSendSelfTape";
import { checkIsAgent } from "shared-auth";
import withRouter, { RouterProps } from "hoc/withRouter";
import {
  generatePath,
  createSearchParams,
  URLSearchParamsInit,
} from "react-router-dom";
import { SELF_TAPE_DETAIL_FULL_PATH } from "config/common";

const UPLOADER_ACCEPT_EXTENSIONS = [
  ...SUPPORTED_VIDEO_EXTENSIONS,
  ...SUPPORTED_AUDIO_EXTENSIONS,
]
  .map((ext) => `.${ext}`)
  .join(",");
const ANY_AMOUNT_VIDEOS_UPLOAD_LIMIT = 10;

export interface ISelfTapeUploadPageTexts
  extends ISelfTapeUploadPageTranslation {
  pageTitle: string;
}

export interface ISubmissionUploadMedia {
  id: string;
  mediaInfoId: string;
  title: string;
}

export interface ISelfTapeUploadPageActions {
  getPageTranslation: GetPageTranslation;
  getSelfTapeUploadPage: GetSelfTapeUploadPage;
  setLoading: SetLoading;
  showPopup: ShowPopup;
  hidePopup: HidePopup;
}

export type ISelfTapeUploadPageStoreProps = RouterProps & {
  texts: ISelfTapeUploadPageTexts;
  locale: string;
  selfTapeRequest: ISelfTapeRequest;
  breadcrumbs: IBreadcrumbBarItem[];
  performerId: string;
  createSubmission: (
    submissionDetails: ISubmissionDetails
  ) => Promise<ISubmission>;
  publishSubmission: (submissionId: string) => Promise<ISubmission>;
};

export interface ISelfTapeUploadPageProps
  extends ISelfTapeUploadPageStoreProps,
    IWithPageDataProps,
    ISelfTapeUploadPageActions {}

export interface ISelfTapeUploadPageState {
  isUploading: boolean;
  confirmPopUpIsOpen: boolean;
  uploadedMedia: ISubmissionUploadMedia[];
}

export class SelfTapeUploadPage extends React.Component<
  ISelfTapeUploadPageProps,
  ISelfTapeUploadPageState
> {
  public readonly state: Readonly<ISelfTapeUploadPageState> = {
    isUploading: false,
    confirmPopUpIsOpen: false,
    uploadedMedia: [],
  };

  public componentDidMount() {
    this.validateSelftapeRequest();
  }

  public render() {
    const {
      texts: { pageTitle },
      breadcrumbs,
      selfTapeRequest,
    } = this.props;
    const { confirmPopUpIsOpen } = this.state;
    const isAgent = checkIsAgent();

    return (
      <section className="flex h-full flex-col">
        <Head>
          <title>{pageTitle}</title>
        </Head>
        <BreadcrumbBar items={breadcrumbs} />
        <StepTracker items={this.generateSteps()} />
        <div className="m-auto mb-10 max-w-[1440px] flex-1 px-3">
          <div className="c-self-tape-upload-page__uploader g-col-lg-12 g-inner-grid">
            {this.renderTitle()}
            {this.renderMediaRequestDescription()}
            {this.renderUploadZone()}
            <div className="g-col-lg-8 border border-grey-fog-1 bg-grey-snow-0 p-2.5 text-xs">
              <Trans
                i18nKey={
                  isAgent
                    ? "common:selfTapes.request.preview.disclaimer.agent"
                    : "common:selfTapes.request.preview.disclaimer.performer"
                }
                components={{
                  a: (
                    <a
                      href="https://www.spotlight.com/the-small-print/terms-conditions/"
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Terms"
                    />
                  ),
                }}
              />
            </div>
          </div>
        </div>
        {this.renderNavigationButtons()}
        <ConfirmSendSelfTape
          selfTapeRequestId={selfTapeRequest.performerRequestId}
          confirmPopUpIsOpen={confirmPopUpIsOpen}
          proceed={async () => {
            await this.handleNextButtonClick();
            this.closeConfirmationPopup();
          }}
          close={this.closeConfirmationPopup}
        />
      </section>
    );
  }

  private closeConfirmationPopup = () =>
    this.setState({ confirmPopUpIsOpen: false });

  private renderTitle() {
    const {
      texts: {
        body: { uploadTitle },
      },
      userType,
      selfTapeRequest: {
        performer: { name },
      },
    } = this.props;

    let title = uploadTitle;

    if (userType === AccountType.Agent) {
      title += ` - ${name}`;
    }

    return (
      <h1 className="c-self-tape-upload-page__uploader-title g-col-lg-12 mb-2.5 h-auto">
        {title}
      </h1>
    );
  }

  private renderMediaRequestDescription() {
    const { t } = i18n;
    const { selfTapeRequest, texts } = this.props;
    const { videosRequired, anyAmount, mediaRequestType } = selfTapeRequest;
    const { anyDescription } = texts.body;
    const numberOfVideos = videosRequired.length;

    return (
      <div className="c-self-tape-upload-page__request-description g-col-lg-8 g-col-sm-12 g-inner-grid mb-10">
        <h3 className="g-col-lg-8 mb-2.5 border-b border-b-grey-light-2 py-2">
          {t("common:selfTapes.label.instruction")}
        </h3>
        <div className="c-self-tape-upload-page__request-description-row-name g-col-lg-2 g-col-md-3 g-col-sm-5">
          {t(
            `common:mediaRequest.description.label.${_camelCase(
              MediaRequestType[mediaRequestType]
            )}.required`
          )}
        </div>
        <div className="g-col-lg-6 g-col-md-5 g-col-sm-7">
          {anyAmount ? anyDescription : numberOfVideos}
        </div>
        {this.renderInstructionsDescription(videosRequired)}
      </div>
    );
  }

  private renderInstructionsDescription(videos: IVideoRequired[]) {
    const { t } = i18n;
    const { selfTapeRequest } = this.props;
    const { anyAmount, mediaRequestType } = selfTapeRequest;

    return videos.map((video, index) => (
      <div key={video.id} className="g-col-lg-8 g-col-sm-12 g-inner-grid">
        <div className="c-self-tape-upload-page__request-description-row-name g-col-lg-2 g-col-md-3 g-col-sm-5">
          {anyAmount
            ? t("common:selfTapes.label.instruction")
            : t(
                `common:selfTapes.label.instruction.${_camelCase(
                  MediaRequestType[mediaRequestType]
                )}`,
                { quantity: index + 1 }
              )}
        </div>
        <div className="g-col-lg-6 g-col-md-5 g-col-sm-7">
          {video.instructions}
        </div>
      </div>
    ));
  }

  private renderUploadZone() {
    const { t } = i18n;
    const {
      texts,
      selfTapeRequest: { videosRequired, anyAmount, mediaRequestType },
    } = this.props;
    const { dropzoneDescriptionAny } = texts.body;

    return (
      <div className="c-self-tape-upload-page__dropzone g-col-lg-12 g-inner-grid">
        <div className="c-self-tape-upload-page__dropzone-description g-col-lg-8 g-col-md-8 g-sm-12 mb-10">
          {anyAmount
            ? dropzoneDescriptionAny
            : t("common:mediaRequest.instruction.description", {
                mediaRequestType: t(
                  `common:mediaRequest.instruction.description.${_camelCase(
                    MediaRequestType[mediaRequestType]
                  )}`
                ),
              })}
        </div>
        <div className="c-self-tape-upload-page__dropzone-upload g-col-lg-6 g-col-md-6 g-sm-12">
          <TitaniumUploader
            multiUpload={true}
            texts={getUploaderTexts(texts)}
            accept={UPLOADER_ACCEPT_EXTENSIONS}
            onFileRemove={this.handleFileRemove}
            onMultipleUploadStart={this.handleMultipleFileUploadStart}
            onCompleteSuccess={this.handleFileUploadCompleted}
            onRejectMultipleFiles={this.handleRejectMultipleFiles}
            onRenameFile={this.handleRenameFile}
            maxUploadLimit={
              anyAmount ? ANY_AMOUNT_VIDEOS_UPLOAD_LIMIT : videosRequired.length
            }
            isProcessingVisible={false}
          />
        </div>
      </div>
    );
  }

  private renderNavigationButtons() {
    const {
      texts: {
        body: { nextButtonText, closeButtonText },
      },
    } = this.props;
    const { isUploading, uploadedMedia } = this.state;

    return (
      <StickyCTABar
        cancel={{
          text: closeButtonText,
          onClick: this.showConfirmationPopup,
          wrapperClassName: "mr-auto",
        }}
        next={{
          text: nextButtonText,
          onClick: () => this.setState({ confirmPopUpIsOpen: true }),
          disabled: isUploading || !uploadedMedia.length,
        }}
        ctaBarAdditionalClassName="max-w-[1440px]"
      />
    );
  }

  private handleRenameFile = (id: string, newName: string) => {
    const updatedUploadedMedia = this.state.uploadedMedia.map((media) =>
      media.id === id ? { ...media, title: newName } : media
    );

    this.setState({ uploadedMedia: updatedUploadedMedia });
  };

  private handleMultipleFileUploadStart = (fileUploads: IFileUpload[]) => {
    const newMedia = fileUploads.map((file) => ({
      id: file.id,
      mediaInfoId: "",
      title: file.file.name ?? "",
    }));

    this.setState({
      uploadedMedia: [...this.state.uploadedMedia, ...newMedia],
      isUploading: true,
    });
  };

  private handleRejectMultipleFiles = () => {
    this.showMediaPopup(MediaErrorPopupType.ExceedLimit);
  };

  private handleFileUploadCompleted = ({
    id,
    mediaInfoId,
  }: ICompleteFileUpload) => {
    const updatedUploadedMedia = this.state.uploadedMedia.map((media) =>
      media.id === id ? { ...media, mediaInfoId } : media
    );
    const isUploading = updatedUploadedMedia.some(
      (media) => media.mediaInfoId.trim() === ""
    );

    this.setState({ uploadedMedia: updatedUploadedMedia, isUploading });
  };

  private handleFileRemove = (deletedFileId: string, callback: () => void) => {
    const updatedUplodedMedia = this.state.uploadedMedia.filter(
      (media) => media.id !== deletedFileId
    );
    const isUploading = updatedUplodedMedia.some(
      (media) => media.mediaInfoId.trim() === ""
    );

    this.setState({ uploadedMedia: updatedUplodedMedia, isUploading }, () =>
      callback()
    );
  };

  private generateSteps() {
    const [uploadVideos, addDetails] = this.props.texts.header.stepTracker;

    return [
      {
        label: String(uploadVideos.Index + 1),
        title: uploadVideos.Label,
        onClick: () => alert("Upload videos step"),
        status: StepTrackerItemStatus.Active,
        isClickable: true,
      },
      {
        label: String(addDetails.Index + 1),
        title: addDetails.Label,
        onClick: () => alert("Add details step"),
        status: StepTrackerItemStatus.Default,
        isClickable: false,
      },
    ];
  }

  private validateSelftapeRequest() {
    const { selfTapeRequest, router } = this.props;
    const { performerRequestId = "" } = router.params;

    const isExpired = dateService.isBeforeNow(
      this.props.selfTapeRequest.expiryDate
    );
    const hasSubmissionMedia = selfTapeRequest.submissionMedia.length > 0;

    if (isExpired && hasSubmissionMedia) {
      router.navigate(
        generatePath(SELF_TAPE_DETAIL_FULL_PATH, {
          performerRequestId,
        })
      );
    }
  }

  private showConfirmationPopup = () => {
    this.props.showPopup({
      type: PopupType.ConfirmationPopup,
      data: {
        texts: getConfirmationPopupTexts(this.props.texts),
        handleProceed: this.handleProceedButtonClick,
        handleCancel: this.props.hidePopup,
      },
    });
  };

  private handleNextButtonClick = async () => {
    const {
      selfTapeRequest: { breakdownId, projectId, videosRequired, anyAmount },
      createSubmission,
      publishSubmission,
    } = this.props;
    const { uploadedMedia } = this.state;
    const maxAmount = anyAmount
      ? ANY_AMOUNT_VIDEOS_UPLOAD_LIMIT
      : videosRequired.length;

    if (uploadedMedia.length === 0) {
      this.showMediaPopup(MediaErrorPopupType.NotUploaded);
    } else if (uploadedMedia.length > maxAmount) {
      this.showMediaPopup(MediaErrorPopupType.ExceedLimit);
    } else {
      this.setState({ isUploading: true });
      const selfTapeSubmissionVideos = uploadedMedia.map((media, index) => {
        return {
          mediaInfoId: media.mediaInfoId,
          requiredMediaId: anyAmount
            ? videosRequired[0].id
            : videosRequired[index].id,
          title: media.title,
        };
      });
      const selfTapeSubmission = {
        breakdownId,
        projectId,
        videos: selfTapeSubmissionVideos,
      };
      const submissionResponse = await createSubmission(selfTapeSubmission);

      brazeLogCustomEvent(BrazeCustomEvents.TapeRequestSubmitted, {
        tape_req_submitted: true,
        tape_req_submitted_date: new Date().toISOString(),
      });

      await publishSubmission(submissionResponse.id);
      this.setState({ isUploading: false });
      this.redirectToSelfTapeRequestPage({
        [UrlCommand.ShowSelfTapesSendingSuccess]: "1",
      });
    }
  };

  private showMediaPopup(popupType: MediaErrorPopupType) {
    this.props.showPopup({
      type: PopupType.ErrorPopup,
      data: {
        ...getMediaPopupTexts(this.props.texts, popupType),
        hidePopup: this.props.hidePopup,
      },
    });
  }

  private handleProceedButtonClick = () => {
    this.props.hidePopup();
    this.redirectToSelfTapeRequestPage();
  };

  private redirectToSelfTapeRequestPage = (
    params: URLSearchParamsInit = {}
  ) => {
    const { router } = this.props;
    const { performerRequestId } = router.params;

    const redirectLink = generatePath(SELF_TAPE_DETAIL_FULL_PATH, {
      performerRequestId,
    });
    const search = createSearchParams(params).toString();

    router.navigate(`${redirectLink}?${search}`);
  };
}

const mapStateToProps = (
  state: IApplicationState,
  ownProps: ISelfTapeUploadPageProps
) => {
  const pageTranslation = state.translation.pages[PageKey.SelfTapeUpload]!;
  const commonTranslation = state.translation.common;
  const selfTapeRequest = state.page.data!.selfTapeRequest!;
  const { router } = ownProps;
  const { performerRequestId = "" } = router.params;

  const breadcrumbs = prepareBreadcrumbs(
    selfTapeRequest,
    myAccountLink,
    performerRequestId
  );

  return {
    texts: prepareTexts(pageTranslation, commonTranslation, selfTapeRequest),
    locale: state.ui.locale,
    selfTapeRequest,
    performerRequestId,
    breadcrumbs,
    createSubmission: (submissionDetails: ISubmissionDetails) =>
      selfTapeRequestService.createSelftapeSubmissionByPerformerRequestId(
        performerRequestId,
        submissionDetails
      ),
    publishSubmission: (submissionId: string) =>
      selfTapeRequestService.publishSelftapeSubmissionByPerformerRequestId(
        performerRequestId,
        submissionId
      ),
  };
};

const pageDataLoaders: ILoaders<ISelfTapeUploadPageProps> = {
  loadTranslation(props, isLocaleChanged) {
    return props.getPageTranslation(
      { pageKey: PageKey.SelfTapeUpload, locale: props.locale },
      { throwOnError: isLocaleChanged }
    );
  },
  loadData(props) {
    const { router, abortController } = props;
    const { performerRequestId = "" } = router.params;

    return props.getSelfTapeUploadPage(performerRequestId, abortController);
  },
};

const mapDispatchToProps: MapDispatchToProps<ISelfTapeUploadPageActions> = {
  getPageTranslation,
  getSelfTapeUploadPage,
  setLoading,
  showPopup,
  hidePopup,
};

export default compose(
  withRouter,
  connect(mapStateToWithPageDataProps, mapDispatchToProps),
  withPageData<ISelfTapeUploadPageProps>(
    PageKey.SelfTapeUpload,
    pageDataLoaders
  ),
  withScrollManager,
  connect(mapStateToProps, mapDispatchToProps)
)(SelfTapeUploadPage);
